import Chip, { ChipVariant } from '@features/ui-atoms/components/Chip';
import { useTranslation } from 'next-i18next';

/**
 * AdminBadge — single source of truth for rendering the admin/superadmin
 * marker next to a user's name. Reads `admin` / `superuser` flags only
 * (no duplicate role logic). Renders nothing for regular users.
 */
interface AdminBadgeUser {
  admin?: boolean;
  superuser?: boolean;
}

interface AdminBadgeProps {
  user: AdminBadgeUser | undefined | null;
  /** Optional test id (each surface scopes its own to keep selectors stable). */
  testId?: string;
}

export const AdminBadge = ({ user, testId }: AdminBadgeProps) => {
  const { t } = useTranslation('common');
  if (!user) return null;

  // superuser wins — a superadmin is always also admin in the seed,
  // and "Superadmin" is the stronger signal to display.
  if (user.superuser) {
    return (
      <span data-testid={testId}>
        <Chip label={t('user.role.superadmin')} variant={ChipVariant.ORANGE} />
      </span>
    );
  }

  if (user.admin) {
    return (
      <span data-testid={testId}>
        <Chip label={t('user.role.admin')} variant={ChipVariant.BLUE} />
      </span>
    );
  }

  return null;
};
